import { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { format, parseISO } from 'date-fns';
import { MapPin, Clock, Users, PencilLine, ExternalLink, Video, Mail, X, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { useCalendarStore } from '@/stores/calendarStore';
import { useCalendarViewStore } from '@/stores/calendarViewStore';
import { useCalendarModalStore } from '@/stores/calendarModalStore';
import { useCalendarActionsStore } from '@/stores/calendarActionsStore';
import type { CalendarEventModel, CalendarParticipant } from '@/types/calendar';

function formatEventTime(event: CalendarEventModel) {
  if (event.allDay) {
    const startLabel = event.startDate ? format(parseISO(`${event.startDate}T00:00:00Z`), 'PPP') : 'All day';
    const endLabel = event.endDate && event.endDate !== event.startDate
      ? format(parseISO(`${event.endDate}T00:00:00Z`), 'PPP')
      : null;
    return endLabel ? `${startLabel} → ${endLabel}` : startLabel;
  }
  const start = event.start?.dateTime ? new Date(event.start.dateTime) : null;
  const end = event.end?.dateTime ? new Date(event.end.dateTime) : null;
  if (start && end) {
    const sameDay = start.toDateString() === end.toDateString();
    if (sameDay) {
      return `${format(start, 'EEE, MMM d · p')} – ${format(end, 'p')}`;
    }
    return `${format(start, 'EEE, MMM d · p')} → ${format(end, 'EEE, MMM d · p')}`;
  }
  if (start) {
    return format(start, 'EEE, MMM d · p');
  }
  return 'Time TBD';
}

function getAttendeeStatusBadge(participant: CalendarParticipant) {
  const status = (participant.responseStatus ?? '').toLowerCase();
  if (status === 'accepted' || status === 'yes') {
    return { label: 'Accepted', variant: 'default' as const };
  }
  if (status === 'declined' || status === 'no') {
    return { label: 'Declined', variant: 'destructive' as const };
  }
  if (status === 'tentative') {
    return { label: 'Tentative', variant: 'secondary' as const };
  }
  return { label: 'Pending', variant: 'outline' as const };
}

function getAnchorStyle(anchor: { x: number; y: number; width: number; height: number } | null) {
  if (typeof window === 'undefined' || !anchor) {
    return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
  }

  const isMobile = window.innerWidth < 640;

  // On mobile, center both horizontally and vertically
  if (isMobile) {
    return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
  }

  // On desktop, anchor to the clicked element
  const preferAbove = anchor.y > window.innerHeight / 2;
  const baseTop = preferAbove ? anchor.y : anchor.y + anchor.height;
  const offset = preferAbove ? -16 : 16;
  const transformY = preferAbove ? '-100%' : '0';
  const top = Math.min(Math.max(baseTop + offset, 16), window.innerHeight - 16);
  const left = Math.min(Math.max(anchor.x + anchor.width / 2, 16), window.innerWidth - 16);
  return { top, left, transform: `translate(-50%, ${transformY})` };
}

export function CalendarEventDetailPopover() {
  const selectedEventId = useCalendarViewStore((state) => state.selectedEventId);
  const event = useCalendarStore((state) => (selectedEventId ? state.eventsById[selectedEventId] : null));
  const calendar = useCalendarStore((state) => (event ? state.calendars[event.calendarUid ?? ''] ?? null : null));
  const anchor = useCalendarViewStore((state) => state.eventDetailAnchor);
  const closeDetail = useCalendarViewStore((state) => state.closeEventDetail);
  const editEvent = useCalendarModalStore((state) => state.editEvent);
  const deleteEvent = useCalendarActionsStore((state) => state.deleteEvent);
  const deletingEventId = useCalendarActionsStore((state) => state.deletingEventId);
  const mutationError = useCalendarActionsStore((state) => state.mutationError);

  useEffect(() => {
    const handleKey = (eventKey: KeyboardEvent) => {
      if (eventKey.key === 'Escape') {
        closeDetail();
      }
    };
    if (selectedEventId) {
      window.addEventListener('keydown', handleKey);
      return () => window.removeEventListener('keydown', handleKey);
    }
    return undefined;
  }, [closeDetail, selectedEventId]);

  const containerStyle = useMemo(() => getAnchorStyle(anchor), [anchor]);

  const handleEdit = () => {
    if (!event) {
      return;
    }
    editEvent(event.id);
  };

  const handleDelete = async () => {
    if (!event) {
      return;
    }
    await deleteEvent({ calendarUid: event.calendarUid, eventId: event.id, sendUpdates: 'all' });
    closeDetail();
  };

  if (!event) {
    return null;
  }

  return createPortal(
    <>
      <div className="fixed inset-0 z-40 bg-black/20" onClick={closeDetail} />
      <div
        className="fixed z-50 w-[calc(100vw-32px)] sm:w-[360px] max-w-[360px] rounded-2xl border border-border/60 bg-background shadow-xl"
        style={containerStyle}
      >
        <div className="flex items-start justify-between gap-3 border-b border-border/40 px-4 py-3">
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Event</p>
            <h2 className="text-lg font-semibold leading-tight">{event.summary || 'Untitled event'}</h2>
            <p className="text-xs text-muted-foreground">{calendar?.summary ?? 'Calendar'}</p>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" onClick={handleEdit} aria-label="Edit event">
              <PencilLine className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={closeDetail} aria-label="Close">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <ScrollArea className="max-h-[60vh]">
          <div className="space-y-4 px-4 py-4">
            <div className="flex items-start gap-3 text-sm">
              <Clock className="mt-0.5 h-4 w-4 text-muted-foreground" />
              <div>
                <p className="font-medium text-foreground">{formatEventTime(event)}</p>
                {!event.allDay && event.start?.timeZone && (
                  <p className="text-xs text-muted-foreground">{event.start.timeZone}</p>
                )}
              </div>
            </div>
            {event.location && (
              <div className="flex items-start gap-3 text-sm">
                <MapPin className="mt-0.5 h-4 w-4 text-muted-foreground" />
                <span className="text-foreground">{event.location}</span>
              </div>
            )}
            {Array.isArray(event.attendees) && event.attendees.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <p className="text-sm font-semibold">Attendees</p>
                </div>
                <div className="space-y-2">
                  {event.attendees.map((attendee) => {
                    if (!attendee.email) {
                      return null;
                    }
                    const status = getAttendeeStatusBadge(attendee);
                    return (
                      <div key={attendee.email} className="flex items-center justify-between rounded-xl border border-border/60 px-3 py-2">
                        <div>
                          <p className="text-sm font-medium">{attendee.displayName || attendee.email}</p>
                          <p className="text-xs text-muted-foreground">{attendee.email}</p>
                        </div>
                        <Badge variant={status.variant}>{status.label}</Badge>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {event.description && (
              <div className="space-y-2">
                <p className="text-sm font-semibold">Notes</p>
                <p className="rounded-xl bg-muted/40 px-3 py-2 text-sm text-muted-foreground whitespace-pre-wrap">
                  {event.description}
                </p>
              </div>
            )}
            {mutationError && (
              <div className="rounded-xl border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
                {mutationError}
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              {event.hangoutLink && (
                <Button variant="outline" size="sm" onClick={() => window.open(event.hangoutLink ?? '#', '_blank')}>
                  <Video className="mr-2 h-4 w-4" /> Join call
                </Button>
              )}
              {event.htmlLink && (
                <Button variant="outline" size="sm" onClick={() => window.open(event.htmlLink ?? '#', '_blank')}>
                  <ExternalLink className="mr-2 h-4 w-4" /> Open in Google
                </Button>
              )}
              {Array.isArray(event.attendees) && event.attendees.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const attendees = event.attendees?.filter((attendee) => attendee.email).map((attendee) => attendee.email) ?? [];
                    if (attendees.length) {
                      window.location.href = `mailto:${attendees.join(',')}?subject=${encodeURIComponent(event.summary ?? 'Event invitation')}`;
                    }
                  }}
                >
                  <Mail className="mr-2 h-4 w-4" /> Email attendees
                </Button>
              )}
            </div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="ghost" size="sm" className="w-full justify-start text-destructive">
                  <Trash2 className="mr-2 h-4 w-4" /> Delete event
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete this event?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Removing the event will notify attendees and cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    onClick={handleDelete}
                    disabled={deletingEventId === event.id}
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </ScrollArea>
      </div>
    </>,
    document.body
  );
}
